import apiClient from './api';
import { toast } from 'react-hot-toast';

interface UploadResponse {
  success: boolean;
  url: string;
  fileName: string;
  fileInfo?: {
    originalName: string;
    contentType: string;
    size: number;
  };
  error?: string;
}

/**
 * Image upload service that handles uploading images to Cloudflare R2
 */
const imageUploadService = {
  /**
   * Upload an image to R2 storage
   * @param file - The file to upload
   * @param onProgress - Optional callback for upload progress
   * @returns Promise resolving to the uploaded image URL
   */
  uploadImage: async (file: File, onProgress?: (progress: number) => void): Promise<string> => {
    try {
      console.log('[ImageUploadService] Starting image upload process', { 
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        lastModified: new Date(file.lastModified).toISOString()
      });
      
      // Check for auth token
      const token = localStorage.getItem('authToken');
      console.log('[ImageUploadService] Auth token exists:', !!token);
      if (token) {
        console.log('[ImageUploadService] Token prefix:', token.substring(0, 10) + '...');
      } else {
        console.warn('[ImageUploadService] No auth token found - upload will fail with 401');
      }
      
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        console.error('[ImageUploadService] File type not allowed:', file.type);
        throw new Error('Unsupported file type. Please upload a JPG, PNG, WEBP, or GIF image.');
      }
      
      // Validate file size (max 5MB)
      const MAX_SIZE = 5 * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        console.error('[ImageUploadService] File too large:', file.size);
        throw new Error('Image exceeds maximum size of 5MB');
      }

      // Create form data
      const formData = new FormData();
      formData.append('image', file);
      
      console.log('[ImageUploadService] Sending request to /upload/image endpoint');
      
      // Make the API request with explicit auth header
      const headers: Record<string, string> = {};
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      console.log('[ImageUploadService] Request headers:', headers);
      
      // Make the API request with progress tracking
      const response = await apiClient.post<UploadResponse>('/upload/image', formData, {
        headers,
        // Important: Don't set Content-Type header for FormData - let browser set it automatically
        transformRequest: [(data) => data], // Prevent axios from setting Content-Type
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log('[ImageUploadService] Upload progress:', progress + '%', {
              loaded: progressEvent.loaded,
              total: progressEvent.total
            });
            onProgress?.(progress);
          }
        }
      });
      
      console.log('[ImageUploadService] Upload response received:', response.data);
      
      if (!response.data.success || !response.data.url) {
        console.error('[ImageUploadService] Upload failed:', response.data.error);
        throw new Error(response.data.error || 'Upload failed');
      }
      
      console.log('[ImageUploadService] Upload successful, returning URL:', response.data.url);
      return response.data.url;
    } catch (error) {
      console.error('[ImageUploadService] Error in upload process:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred during upload';
      toast.error(errorMessage);
      throw error;
    }
  },
  
  /**
   * Convert a file to a local URL for preview
   * @param file - The file to convert
   * @returns Local URL for the file
   */
  getLocalImageUrl: (file: File): string => {
    const url = URL.createObjectURL(file);
    console.log('[ImageUploadService] Created local image URL for preview:', {
      url,
      fileType: file.type,
      fileSize: file.size
    });
    return url;
  },
  
  /**
   * Clean up a local object URL
   * @param url - The URL to clean up
   */
  revokeLocalImageUrl: (url: string): void => {
    URL.revokeObjectURL(url);
    console.log('[ImageUploadService] Revoked local image URL:', url);
  }
};

export default imageUploadService; 